
import { BaseViewModel, IEntityConstructorOptions } from "./Base/BaseViewModel";
import { DataEntityBase, Models } from "../Services/DataService/Model";
import { ModelManager } from "./ModelManager";
import { TutorialState, TutorialStateProperties } from "../Services/DataService/Model/TutorialState";
import { ACTIONS, Callback, Step } from "react-joyride";
import { CurrentDaysFocus } from "../Services/DataService/Model/DailyEntry";
import { JournalViewModel } from "./JournalViewModel";
import { DailyEntryViewModel } from "./DailyEntryViewModel";
import { NavigateFunction } from "react-router";
import { JournalStrings } from "../AppRoutes/Journal/SharedComponents/JournalStrings";
import { BasicEntityWatcher } from "../Services/DataService/EntityWatcher";
import { IConstructorOptions } from "../Utilities";
import { ICorrelation } from "./Utilities";

const KeyName = "TutorialState";

export interface GLStep extends Step {
    targetRoute?: string;
    currentFocus?: CurrentDaysFocus;
    borderRadius?: string;
}

export class TutorialStateViewModel extends BaseViewModel {

    public static get TypeName() { return Models.TutorialState; };
    public get TypedData() { return ((this.Data as unknown) as TutorialState); }


    public static Manager = new ModelManager<TutorialStateViewModel>({
        createFN:
            async (options?: IEntityConstructorOptions) => {
                var retVal = new TutorialStateViewModel(options);
                return retVal;
            },
        idFromKeyFN: (_key: string) => TutorialStateViewModel.CreateId(),
        typeName: Models.TutorialState,
    });


    public static Options() {
        return DataEntityBase.OptionsFromId(TutorialStateViewModel.CreateId());
    }

    public static CreateKey() {
        return KeyName;
    }

    public static CreateId() {
        return DataEntityBase.CreateId(TutorialStateViewModel.TypeName, KeyName)
    };

    protected CreateData(watcher: BasicEntityWatcher, options?: IConstructorOptions) {
        return TutorialState.Create(watcher, options);
    }







    protected onReplacedData(_oldData?: DataEntityBase, _newData?: DataEntityBase) {
        this.journalVM = undefined;
    }

    protected async doHydrate(_correlation: ICorrelation, _isRehydrate?: boolean): Promise<void> {
        // No work to do, but need to override anyway.
    }

    private constructor(options?: IEntityConstructorOptions) {
        super(options);

        this.SetHydrated();

        this.shouldTrackChanges = this.TypedData.isRunning;
        this.TypedData.PropertyChanged.Subscribe((e) => {
            if (e.property === TutorialStateProperties.isRunning || e.property === 'isHydrated')
                this.shouldTrackChanges = this.TypedData.isRunning
        });
        this.PropertyChanged.Subscribe((e) => {
            if (e.property === 'Data')
                this.shouldTrackChanges = this.TypedData.isRunning
        });

        const observer = new MutationObserver((mutationList) => {
            if (this.shouldTrackChanges === false)
                return;

            for (const mutation of mutationList) {
                if (mutation.type === "childList") {
                    for (const node of mutation.addedNodes) {
                        if (node instanceof Element && node.classList.contains('gl-tutorial-prevent-run')) {
                            this.TypedData.isRunning = false;
                            this.shouldTrackChanges = true;
                        }
                    }

                    for (const node of mutation.removedNodes) {
                        if (node instanceof Element && node.classList.contains('gl-tutorial-prevent-run')) {
                            this.TypedData.isRunning = true;
                        }
                    }
                    console.log("A child node has been added or removed.");
                }

                if (mutation.type === "attributes") {
                    console.log(`The ${mutation.attributeName} attribute was modified.`);
                }
            }
        });

        observer.observe(document.body, {
            attributes: true,
            childList: true,
            //   subtree: true
        });

        // this.gratefulItems.PropertyChanged.Subscribe(this.onActionItemsChanged);
    }


    private get todayVM(): DailyEntryViewModel | undefined {
        return this.journalVM?.TodaysDailyEntry;
    };

    public setJournalViewModel(journalVM: JournalViewModel) {
        this.journalVM = journalVM;
        if (this.TypedData.stepIndex === 0) {
            this.TypedData.previousFocus = this.todayVM?.TypedData.viewCurentFocus ?? CurrentDaysFocus.Morning;
        }
    }

    public reset = () => {
        this.TypedData.stepIndex = 0;
        this.TypedData.isRunning = true;
        this.TypedData.previousFocus = this.todayVM?.TypedData.viewCurentFocus ?? CurrentDaysFocus.Morning;
        if (this.navigate)
            this.navigate(this.steps[0]?.targetRoute ?? '/journal/home');
        else
            console.warn("Navigate function not set -- cannot navigate to target route during reset");
    }

    public set navigateFunction({ navigate, pathname }: { navigate: NavigateFunction, pathname: string }) {
        this.navigate = navigate;


        let step = this.steps[this.TypedData.stepIndex];
        if (this.TypedData.isRunning && step.targetRoute !== undefined && pathname.indexOf(step.targetRoute) === -1) {
            setTimeout(() => {
                this.navigate!(step.targetRoute!);
            }, 1);
        }
    }



    public get steps(): GLStep[] {
        return [
            {
                target: 'body',
                title: JournalStrings.Tutorial.Step1.Title,
                content: (
                    <div>
                        <p>{JournalStrings.Tutorial.Step1["Content-0"]}</p>
                        <p>{JournalStrings.Tutorial.Step1["Content-1"]}</p>
                    </div>
                ),
                disableBeacon: true,
                placement: 'center',
                targetRoute: '/journal/home'
            },
            {
                target: '.gl-today-section',
                title: JournalStrings.Tutorial.Step2.Title,
                content: (
                    <>
                        <p>{JournalStrings.Tutorial.Step2["Content-0"]}</p>
                        <p>{JournalStrings.Tutorial.Step2["Content-1"]}</p>
                    </>
                ),
                disableBeacon: true,
                offset: 0,
                currentFocus: CurrentDaysFocus.Execution
            },
            {
                target: 'body',
                title: JournalStrings.Tutorial.Step3.Title,
                content: (
                    <>
                        <p>{JournalStrings.Tutorial.Step3["Content-0"]}</p>
                        <p>{JournalStrings.Tutorial.Step3["Content-1"]}</p>
                    </>
                ),
                disableBeacon: true,
                placement: 'center',
                targetRoute: this.todayVM?.Route,
                currentFocus: CurrentDaysFocus.Morning
            },
            {
                target: '.gl-today-grateful-header',
                title: JournalStrings.Tutorial.Step4.Title,
                content: (
                    <>
                        <p>{JournalStrings.Tutorial.Step4["Content-0"]}</p>
                        <p>{JournalStrings.Tutorial.Step4["Content-1"]}</p>
                        <p><span className='gl-bold'>{JournalStrings.Tutorial.Step4["Content-2"]}</span></p>
                    </>
                ),
                disableBeacon: true,
                offset: 0,
                currentFocus: CurrentDaysFocus.Morning
            }
        ];
    }

    public onCallback: Callback =
        (data) => {


            if (data.lifecycle === 'complete') {

                switch (data.action) {

                    case ACTIONS.PREV:
                        this.TypedData.stepIndex = this.TypedData.stepIndex - 1;
                        if (this.TypedData.stepIndex < 0)
                            this.TypedData.stepIndex = 0;
                        break;

                    case ACTIONS.NEXT:
                        this.TypedData.stepIndex = this.TypedData.stepIndex + 1;
                        if (this.TypedData.stepIndex >= this.steps.length) {
                            this.exitTutorial();
                            return;
                        }
                        break;

                    case ACTIONS.CLOSE:
                        this.exitTutorial();
                        return;

                    case ACTIONS.SKIP:
                        this.exitTutorial();
                        return;

                    default:
                        break;
                }



                if (this.todayVM === undefined)
                    throw new Error("Today's Daily Entry is not set");

                let step = this.steps[this.TypedData.stepIndex];
                this.todayVM.TypedData.viewCurentFocus = step.currentFocus ?? this.TypedData.previousFocus;


                if (step.targetRoute !== undefined) {
                    this.navigate!(step.targetRoute);
                }

            }
        }



    public exitTutorial = () => {
        this.TypedData.stepIndex = 0;
        this.TypedData.isRunning = false;
        if (this.todayVM)
            this.todayVM.TypedData.viewCurentFocus = this.TypedData.previousFocus;
    }


    private journalVM: JournalViewModel | undefined = undefined;
    private navigate: NavigateFunction | undefined = undefined;
    private shouldTrackChanges: boolean = true;
}

